import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { COOKIE_CONSENT_KEY } from '~/services/WebAnalyticsService';

const META_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID as string;

let pixelInitialized = false;

/**
 * Dynamically loads the Meta Pixel (fbevents.js) script and initializes the pixel.
 * Does NOT fire PageView — that is owned exclusively by usePageView().
 * Called only after user consents to tracking.
 */
function loadMetaPixel(): void {
  if (pixelInitialized) return;
  if (!META_PIXEL_ID) return;
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
  const w = window as unknown as Record<string, unknown>;
  if (w.fbq) return;
  const n: ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[];
    loaded: boolean;
    version: string;
    push: (...args: unknown[]) => void;
  } = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod(...args);
    } else {
      n.queue.push(args);
    }
  } as ReturnType<typeof Object>;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  const fbq = w.fbq as (...args: unknown[]) => void;
  fbq('init', META_PIXEL_ID);

  pixelInitialized = true;
}

function isConsentGiven(): boolean {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted';
}

interface MetaPixelEvent {
  name: string;
  data?: Record<string, unknown>;
}

const fbqTrack = (event: MetaPixelEvent): void => {
  if (!isConsentGiven()) return;
  const fbq = (window as unknown as Record<string, unknown>).fbq;
  if (fbq && typeof fbq === 'function') {
    (fbq as (...args: unknown[]) => void)('track', event.name, event.data);
  }
};

/**
 * Revokes Meta Pixel consent, clears tracking cookies (_fbp, _fbc),
 * removes the fbevents.js script from DOM, and resets initialization state.
 */
function disableMetaPixel(): void {
  const w = window as unknown as Record<string, unknown>;

  // Revoke consent if fbq is loaded
  if (w.fbq && typeof w.fbq === 'function') {
    (w.fbq as (...args: unknown[]) => void)('consent', 'revoke');
  }

  // Clear _fbp and _fbc cookies for all possible path/domain combinations
  const hostname = window.location.hostname;
  const domainWithDot = '.' + hostname;
  const cookieNames = ['_fbp', '_fbc'];
  const paths = ['/'];
  const domains = ['', hostname, domainWithDot];

  for (const name of cookieNames) {
    for (const path of paths) {
      for (const domain of domains) {
        const domainPart = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainPart}`;
      }
    }
  }

  // Remove fbevents.js script from DOM
  const scripts = document.querySelectorAll('script[src*="connect.facebook.net"]');
  scripts.forEach((script) => script.parentNode?.removeChild(script));

  pixelInitialized = false;
}

const fbqTrackPageView = (): void => fbqTrack({ name: 'PageView' });

const _fbqTrackCompleteRegistration = (status: boolean): void =>
  fbqTrack({
    name: 'CompleteRegistration',
    data: { status },
  });

/**
 * Hook that fires a Meta Pixel PageView event on every SPA route change.
 * Initializes the pixel on first call if not yet done (e.g. user accepted consent
 * mid-session). Only fires when user has given cookie consent.
 * Should be placed in the main Dashboard layout component.
 */
const usePageView = (): void => {
  const location = useLocation();
  useEffect(() => {
    if (!isConsentGiven()) return;
    if (!pixelInitialized) {
      loadMetaPixel();
    }
    fbqTrackPageView();
  }, [location]);
};

export { usePageView, loadMetaPixel, disableMetaPixel };
